/**
 * @fileoverview Create-class page. Mirrors the mobile ClassCreateScreen —
 * an identity card (kind + name), kind radios, and a roster built via the
 * reusable AddStudentsDialog. Saves via POST /classes then navigates to the
 * new class detail page.
 */

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AddStudentsDialog from "@/components/AddStudentsDialog";
import { CLASS_KINDS, getClassKind } from "@/lib/classKinds";
import { MARKETPLACE_ENABLED } from "@/lib/featureFlags";
import api, { getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Sidebar, MobileNav } from "@/components/Sidebar";

const ClassCreate = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kindId, setKindId] = useState("studio_class");
  const [memberIds, setMemberIds] = useState([]);
  const [students, setStudents] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    api
      .get("/students", { signal: controller.signal })
      .then((res) => setStudents(res.data || []))
      .catch(() => {});
    // The same source the record screen and slot dialog attribute from, so a
    // class is attributed the way everything else already is.
    if (MARKETPLACE_ENABLED) {
      api
        .get("/marketplace/my-programs", { signal: controller.signal })
        .then((res) => setPrograms(res.data?.programs || []))
        .catch(() => {});
    }
    return () => controller.abort();
  }, []);

  const kind = getClassKind(kindId);
  const canSave = name.trim().length > 0;

  const memberStudents = useMemo(
    () => memberIds.map((id) => students.find((s) => s.student_id === id)).filter(Boolean),
    [memberIds, students]
  );

  // Remove a student from the draft roster with an undo toast, mirroring the
  // recoverable removal on the live ClassDetail page so the two roster screens
  // behave consistently (no silent one-click loss).
  const handleRemoveMember = (student) => {
    setMemberIds((ids) => ids.filter((id) => id !== student.student_id));
    toast(`Removed ${`${student.first_name || ""} ${student.last_name || ""}`.trim() || "student"}`, {
      action: {
        label: "Undo",
        onClick: () =>
          setMemberIds((ids) => (ids.includes(student.student_id) ? ids : [...ids, student.student_id])),
      },
    });
  };

  const handleSave = async () => {
    if (!canSave || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/classes", {
        name: name.trim(),
        kind: kindId,
        member_ids: memberIds,
        // Set once, at creation. Attribution is what the program rollups and
        // the weekly emails read, so it is not editable afterwards.
        ...(programId ? { program_id: programId } : {}),
      });
      navigate(`/classes/${data.class_id}`, { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not create class"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--studio-base)]">
      <Sidebar active="students" />
      <MobileNav active="students" />

      <main className="p-4 md:p-8 page-with-nav" data-testid="class-create">
        <Button
          variant="ghost"
          onClick={() => navigate("/students")}
          className="text-[var(--text-tertiary)] active:text-[var(--text-primary)] mb-4 md:mb-6 -ml-2 touch-target"
          data-testid="class-create-back"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Cancel
        </Button>

        <div className="max-w-2xl">
          <p className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-1">New class</p>
          <h1 className="text-2xl md:text-4xl font-light tracking-tight text-[var(--text-primary)] mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            Create a class
          </h1>

          {/* Identity card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-[20px] p-5 md:p-6 mb-6"
          >
            <div className="mb-3 text-[11px] font-semibold tracking-widest uppercase text-[var(--text-tertiary)]">
              {kind.label}
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Class name"
              className="bg-transparent border-0 text-2xl md:text-3xl font-light text-[var(--text-primary)] px-0 h-auto focus-visible:ring-0 placeholder:text-[var(--text-quaternary)]"
              data-testid="class-name-input"
              autoFocus
              maxLength={120}
            />
            <div className="flex items-center gap-1.5 mt-2 text-[var(--text-tertiary)] text-sm">
              <Users className="w-3.5 h-3.5" />
              {memberIds.length} {memberIds.length === 1 ? "student" : "students"}
            </div>

            {/* Attribution is set once, here. It decides which program's
                rollups and weekly emails this class's teaching lands in, and
                is deliberately not editable afterwards — changing it later
                would reattribute teaching that already happened. Only shown
                when the instructor actually teaches in a program. */}
            {programs.length > 0 && (
              <label className="mt-4 block">
                <span className="font-sans text-sm text-[var(--text-secondary)]">Program</span>
                <select
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                  data-testid="class-program-select"
                  className="mt-1 h-11 w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]"
                >
                  <option value="">No program</option>
                  {programs.map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {p.title}{p.org_name ? ` · ${p.org_name}` : ""}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block font-sans text-[12px] text-[var(--text-tertiary)]">
                  Chosen once — it can&rsquo;t be changed after the class exists.
                </span>
              </label>
            )}
          </motion.div>

          {/* Class type */}
          <h2 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Class type</h2>
          <div className="space-y-2 mb-6">
            {CLASS_KINDS.map((k) => {
              const sel = k.id === kindId;
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setKindId(k.id)}
                  role="radio"
                  aria-checked={sel}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-colors ${
                    sel
                      ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-faint)]"
                      : "border-[var(--studio-divider)] bg-[var(--studio-raised)]"
                  }`}
                  data-testid={`class-kind-${k.id}`}
                >
                  {/* Selected in the brand yellow, never a colour of the kind's own. */}
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      sel ? "border-[var(--accent-yellow)]" : "border-[var(--studio-divider-strong)]"
                    }`}
                  >
                    {sel && <span className="w-2.5 h-2.5 rounded-full bg-[var(--accent-yellow)]" />}
                  </span>
                  <span className="flex-1">
                    <span className="block text-[var(--text-primary)] text-sm font-medium">{k.label}</span>
                    <span className="block text-[var(--text-tertiary)] text-xs mt-0.5">{k.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Roster */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Roster · {memberIds.length}</h2>
            <button
              onClick={() => setPickerOpen(true)}
              className="text-[var(--accent-yellow-text)] text-sm flex items-center gap-1"
              data-testid="class-create-add-students"
            >
              <Plus className="w-4 h-4" /> Add students
            </button>
          </div>

          {memberStudents.length === 0 ? (
            <button
              onClick={() => setPickerOpen(true)}
              className="w-full border border-dashed border-[var(--studio-divider-strong)] rounded-2xl p-8 text-center text-[var(--text-tertiary)] hover:bg-[var(--studio-overlay)] transition-colors mb-6"
              data-testid="class-create-empty-roster"
            >
              <Users className="w-6 h-6 text-[var(--text-quaternary)] mx-auto mb-2" />
              <p className="text-sm">Add students from your roster</p>
            </button>
          ) : (
            <div className="space-y-2 mb-6">
              {memberStudents.map((s) => (
                <div
                  key={s.student_id}
                  className="flex items-center gap-3 p-3 bg-[var(--studio-raised)] rounded-xl"
                  data-testid={`class-create-member-${s.student_id}`}
                >
                  <div className="w-9 h-9 bg-[var(--studio-interactive)] rounded-full flex items-center justify-center text-[var(--accent-yellow-text)] font-medium shrink-0">
                    {(s.first_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--text-primary)] text-sm font-medium truncate">{`${s.first_name || ""} ${s.last_name || ""}`.trim()}</p>
                    {s.instruments?.length > 0 && (
                      <p className="text-[var(--text-tertiary)] text-xs truncate">{s.instruments.join(" · ")}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveMember(s)}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    aria-label={`Remove ${s.first_name}`}
                    data-testid={`class-create-remove-${s.student_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={!canSave || submitting}
            className="w-full h-12 bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full active:scale-95 transition-transform disabled:opacity-50 touch-target"
            data-testid="class-create-save"
          >
            {submitting
              ? "Creating..."
              : memberIds.length > 0
                ? `Create class · ${memberIds.length} ${memberIds.length === 1 ? "student" : "students"}`
                : "Create class"}
          </Button>
        </div>
      </main>

      <AddStudentsDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        existingIds={memberIds}
        onConfirm={(ids) => {
          setMemberIds([...memberIds, ...ids.filter((id) => !memberIds.includes(id))]);
          setPickerOpen(false);
        }}
      />
    </div>
  );
};

export default ClassCreate;
